import container from '@/container';
import {
    FetchServiceContract,
    CONTRACTS,
    AuthServiceContract
} from '@/core/contracts';

/**
 * Generate random guid.
 * @export
 * @returns {string}
 */
export function guid(): string {
    const s4 = (): string => {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    };
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

/**
 * Returns valid window.location.origin in IE11-.
 * @export
 * @returns {string}
 */
export function getLocationOrigin(): string {
    const location = window.location;
    let origin = location.origin;
    if (!origin) {
        origin = `${location.protocol}/${location.hostname}`;
        if (location.port) {
            origin += location.port;
        }
    }
    return origin;
}

/**
 * Returns application base url bases on BASE_URL from env.
 * @export
 * @returns {string}
 */
export function getBaseUrl(): string {
    const origin = getLocationOrigin();
    return `${origin}${process.env.BASE_URL}`;
}

/**
 * Encode URL parameters.
 * @export
 * @param {any} dict
 * @returns {string}
 */
export function encodeParams(dict: any): string {
    const result: string[] = [];

    for (const key in dict) {
        if (dict.hasOwnProperty(key)) {
            result.push(
                `${encodeURIComponent(key)}=${encodeURIComponent(dict[key])}`
            );
        }
    }

    return result.join('&');
}

/**
 * Function for unhandled cases.
 * @export
 * @param {never} object
 * @returns {never}
 */
export function assertNever(object: never): never {
    throw new Error('Unhandled case: ' + JSON.stringify(object));
}

/**
 * Lookup field from collection.
 * @export
 * @param {*} object
 * @param {string} field
 * @param {*} collection
 * @returns {*}
 */
export function lookupField(object: any, field: string, collection: any): any {
    if (!collection) {
        return object;
    }

    const fieldId = object[field + 'Id'];

    if (fieldId !== undefined) {
        object[field] = collection[fieldId];
    }

    return object;
}

/**
 * Transform list field into string from collection.
 * @export
 * @param {*} object
 * @param {string} field
 * @param {*} collection
 * @returns {*}
 */
export function getStringFromList(
    object: any,
    field: string,
    collection: any
): any {
    const fieldId = object[field + 'Id'];
    if (fieldId !== undefined) {
        const listNames: string[] = [];

        for (const id of fieldId) {
            listNames.push(collection[id].name);
        }

        object[field] = listNames.join(', ');
    }
    return object;
}

/**
 * Capilatize first letter in string.
 * @export
 * @param {string} str
 * @returns {string}
 */
export function upperFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Round number until 2-4 symbols.
 * Old version of 'shorten' function.
 * @export
 * @param {number} value
 * @returns {string}
 */
export function shorten(value: number): string {
    if (isNaN(value)) {
        return value.toString();
    }

    value = Math.round(value * 100) / 100;
    const valueString = String(value);
    const splittedString = valueString.split('.');
    let result = valueString;

    if (splittedString[0].length > 9) {
        result = splittedString[0].substring(0, splittedString[0].length - 9);

        if (result === '-') {
            result = '-0';
        }

        if (result.length < 3) {
            result =
                result + '.' + splittedString[0][splittedString[0].length - 9];
        }

        return result + 'G';
    } else if (splittedString[0].length > 6) {
        result = splittedString[0].substring(0, splittedString[0].length - 6);

        if (result === '-') {
            result = '-0';
        }

        if (result.length < 3) {
            result =
                result + '.' + splittedString[0][splittedString[0].length - 6];
        }

        return result + 'M';
    } else if (splittedString[0].length > 3) {
        result = splittedString[0].substring(0, splittedString[0].length - 3);

        if (result === '-') {
            result = '-0';
        }

        if (result.length < 3) {
            result =
                result + '.' + splittedString[0][splittedString[0].length - 3];
        }

        return result + 'K';
    } else {
        if (splittedString.length > 1) {
            return splittedString[0] + '.' + splittedString[1][0];
        } else {
            return splittedString[0];
        }
    }
}

/**
 * Place text in user clipboard
 * @param {string} text - text for copying
 */
export function copyToClipboard(text: string): void {
    const element = document.createElement('textarea');
    element.value = text;
    document.body.appendChild(element);
    element.focus();
    element.setSelectionRange(0, element.value.length);
    document.execCommand('copy');
    document.body.removeChild(element);
}

/**
 * Download data from url as file.
 * @export
 * @param {string} url
 * @param {string} filename
 */
export async function download(url: string, filename: string) {
    const fetchService = container.get<FetchServiceContract>(
        CONTRACTS.FetchService
    );
    return fetchService
        .fetch(url)
        .then(response => response.blob())
        .then(blob => {
            // IE & Edge fix for downloading blob files
            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                window.navigator.msSaveBlob(blob, filename);
            } else {
                const objectUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = objectUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
            }
        });
}

/**
 * Upload data to url as file.
 * @export
 * @param {string} url
 * @param {string} filename
 * @param {(event: ProgressEvent<XMLHttpRequestEventTarget>) => void | null} progressListener?
 * @param {number} requestTimeout?
 */
export function uploadFile<T>(
    file: File,
    url: string,
    progressListener?: (
        event: ProgressEvent<XMLHttpRequestEventTarget>
    ) => void | null,
    requestTimeout?: number | null
): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const data = new FormData();
        data.append('File', file);

        const request = new XMLHttpRequest();

        if (requestTimeout) {
            request.timeout = requestTimeout;
        }

        if (progressListener !== null) {
            request.upload.addEventListener(
                'progress',
                progressListener!,
                false
            );
        }

        request.upload.onabort = request.upload.ontimeout = reject;
        request.onreadystatechange = () => {
            if (request.readyState === 4) {
                if (request.status !== 200 && request.status !== 204) {
                    return reject(request.response);
                } else {
                    if (request.status === 204) {
                        return resolve();
                    }
                    let result: any = null;
                    try {
                        result = JSON.parse(request.responseText) as T;
                    } catch (ex) {
                        // Ignore
                    }

                    return resolve(result);
                }
            }
        };

        const authService = container.get<AuthServiceContract>(
            CONTRACTS.AuthService
        );
        const token = authService.accessToken;

        request.open('POST', url, true);
        request.setRequestHeader('Authorization', 'Bearer ' + token);
        request.send(data);
    });
}

/**
 * Get distance on geo between two coordinations.
 * @export
 * @param {number} lat1
 * @param {number} lon1
 * @param {number} lat2
 * @param {number} lon2
 */
export function geoDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
): number {
    const deg2Rad = (deg: number) => {
        return (deg * Math.PI) / 180;
    };

    const r = 6371; // Radius of the earth in km
    const dLat = deg2Rad(lat2 - lat1);
    const dLon = deg2Rad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2Rad(lat1)) *
            Math.cos(deg2Rad(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = r * c;
    return d;
}

export function makeRequest(method: string, url: string): Promise<any> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, url);

        xhr.onload = function() {
            if (this.status >= 200 && this.status < 300) {
                resolve(xhr.response);
            } else {
                reject({
                    status: this.status,
                    statusText: xhr.statusText
                });
            }
        };

        xhr.onerror = function() {
            reject({
                status: this.status,
                statusText: xhr.statusText
            });
        };

        xhr.send();
    });
}
